import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import type { ProductRegimeProductModel } from 'autogen/swagger/all';
import * as transKey from 'autogen/translation-keys/trans-root-website';
import Card from 'components/generic/card';
import { StockNotificationButtonCompact } from 'components/generic/productCTA/components/OpenStockNotification/StockNotificationButton';
import { Typography } from 'components/generic/typography';
import Checkbox from 'components/shared/inputs/checkbox';
import HorizontalProductItem from 'components/shared/productItem/components/HorizontalProductItem/HorizontalProductItem';
import { createProductItemTrackingContext } from 'components/shared/productItem/shared/helpers';
import { firstCharacterToLower } from 'modules/helpers/strings';
import { useRegime } from './Regime';
import RegimeVariantSelector from './RegimeVariantSelector';

// #region Styles

const StyledCard = styled(Card)`
    border: 1px solid ${({ theme }) => theme.ColorBorderTertiary};
`;

const StyledCardHeader = styled(Card.Header)`
    justify-content: center;
    border-bottom: 1px solid ${({ theme }) => theme.ColorBorderTertiary};
    background-color: ${({ theme }) => theme.ComponentsProductCardOverlay};
`;

const StyledCardContent = styled(Card.Content)`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
`;

const StyledCardFooter = styled(Card.Footer)`
    flex-direction: column;
    border-top: 1px solid ${({ theme }) => theme.ColorBorderTertiary};
    background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
    padding: ${({ theme }) => theme.Spacing16};
`;

const CheckboxContainer = styled.label`
    display: flex;
    position: absolute;
    right: ${({ theme }) => theme.Spacing16};
    top: ${({ theme }) => theme.Spacing16};
    z-index: 2;
`;

const StyledProductItem = styled(HorizontalProductItem)<{ isSoldOut: boolean }>`
    ${({ isSoldOut }) =>
        isSoldOut &&
        css`
            opacity: 0.4;
        `}
`;

const StyledButton = styled(StockNotificationButtonCompact)<{ hasVariants: boolean }>`
    position: absolute;
    right: ${({ theme }) => theme.Spacing16};
    bottom: ${({ hasVariants, theme }) => (hasVariants ? theme.Spacing72 : theme.Spacing16)};
    width: 40px;
`;

const StyledVariantSelector = styled(RegimeVariantSelector)`
    padding: ${({ theme }) => theme.Spacing16};
`;

const StyledBody = styled(Typography.Body)<{ isSoldOut: boolean }>`
    ${({ isSoldOut }) =>
        isSoldOut &&
        css`
            opacity: 0.4;
        `}
`;

// #endregion Styles

export default function RegimeCardHorizontal({ product }: { product: ProductRegimeProductModel }) {
    const { product: productItem, sectionInfo, variants, stockAvailable } = product;
    const { isSelected, toggleProduct, mainProductRegimeId, deselectProduct } = useRegime();
    const selected = isSelected(productItem.id);
    const { t } = useTranslation();
    const isCurrentProduct = productItem.id === mainProductRegimeId;
    const trackingContext = createProductItemTrackingContext(firstCharacterToLower(productItem.uaTrackingCategory));

    useEffect(() => {
        if (!stockAvailable) {
            deselectProduct(productItem.id);
        }
    }, [stockAvailable, deselectProduct, productItem]);

    return (
        <StyledCard>
            {isCurrentProduct && (
                <StyledCardHeader size="sm">
                    <Typography.Body size="S" strong as="span">
                        {t(transKey.ProductRegime.CurrentProduct)}
                    </Typography.Body>
                </StyledCardHeader>
            )}

            <StyledCardContent>
                <StyledProductItem model={productItem} themeType="regime" isSoldOut={!stockAvailable} />

                {!stockAvailable && (
                    <StyledButton
                        hasVariants={variants.length > 1}
                        params={{
                            action: productItem.action,
                            trackingContext,
                            productId: productItem.id,
                            externalProductId: productItem.externalProductId,
                            productNavigationLink: productItem.linkAction,
                            fromPowerstep: false,
                            showPowerstepOnAddToBasket: false,
                            addToBasketType: productItem.addToBasketType
                        }}
                        trackingInfo={trackingContext}
                    />
                )}

                <CheckboxContainer>
                    <Checkbox
                        value={productItem.id}
                        checked={selected && stockAvailable}
                        disabled={!stockAvailable}
                        onChange={() => toggleProduct(productItem.id)}
                    />
                </CheckboxContainer>

                {variants.length > 1 && <StyledVariantSelector product={product} />}
            </StyledCardContent>

            {sectionInfo.body && (
                <StyledCardFooter size="sm">
                    <StyledBody size="S" forwardedAs="div" isSoldOut={!stockAvailable}>
                        {sectionInfo.body}
                    </StyledBody>
                </StyledCardFooter>
            )}
        </StyledCard>
    );
}
